import { BuildingTutorial } from "./building_tutorial"
import { SprintTutorial } from "./sprint_tutorial"
import { ForagerTutorial } from "./forager_tutorial"
import { LadderTutorial } from "./ladder_tutorial"
import { TutorialDemo, TutorialUse } from "./tutorial_demo"
import { EmberClueType, isEmberClueType, TutorialType } from "game-common/models"
import { Container, Graphics, Point, Rectangle } from "pixi.js"
import { isMobile } from "../../../../client_util"
import { getUiMetrics } from "./ui_metrics"

import { GuiManager } from "../gui_manager"
import {
    CLUE_LEAVE_MS,
    ClueCallout,
    ClueLight,
    ClueMark,
    clueNotePose,
    ClueRect,
    ClueRing,
    ClueRingTarget,
    ClueScrim,
    ClueSpotlightArea,
    SPOTLIGHT_IN_MS,
    SPOTLIGHT_OUT_MS,
    spotlightHoles,
} from "./clue_callout"
import {
    clueBoxLayout,
    CLUE_NOTCH_LEN,
    clueNotch,
    ClueStop,
    CLUE_TEXT,
    CluePlacement,
    EmberClueDirector,
    routeTutorial,
} from "./ember_clues"

// How long an overworld popup stays up. Tuned for a notice nobody is waiting on: it says its piece
// and gets out of the way. (A clue's much longer lifetime lives in ember_clues.ts, with the rest of
// the clue bookkeeping.)
const POPUP_TIMEOUT_MS = 7000

// A frame hitch (a room load, a GC pause) must not swallow a clue's entrance: the clock a clue is
// posed from never jumps by more than this in one frame.
const MAX_FRAME_MS = 50

// How far past the viewport's edges the dimming behind a helper window reaches, so no sliver of
// the screen is left lit by rounding or a letterbox nudge.
const DEMO_SCRIM_BLEED = 64

// The control bar's inventory button, as the existing controlbar popup boxes it: the first entry in
// ControlBar.setup() (inventory.png), measured from the bar's own x in the gui surface.
const CONTROLBAR_ITEMS_OFFSET_X = 15
const CONTROLBAR_ITEMS_W = 32
const CONTROLBAR_ITEMS_H = 30

// Where a clue points, handed over by whatever drew the control.
//
// The rect is in `host` coordinates and the clue is drawn INTO `host` -- not into the gui surface --
// because most of these controls live inside the inventory modal, which is a separate container on
// the stage. Drawing into the target's own container is what keeps a clue in the right coordinate
// space and on the right side of the z-order, and is the same trick setupCompanionTutorial uses to
// hang its box off an entity's renderable.
export interface ClueAnchor {
    host: Container
    x: number
    y: number
    w: number
    h: number
    // Which side of the control the box goes on. Defaults to "left", which suits the top-right HUD row
    // and the docked panel. The ready bar is in the bottom-left corner with nothing to its left, so it
    // asks for "above" -- clamped beside it, the box landed on top of the thing it was pointing at.
    placement?: CluePlacement
    // Mark the control with arcs round a CIRCLE rather than the default corner brackets. For the
    // countdown dial, which is round: brackets on its bounding box read as a misaligned box rather
    // than as a pointer at the thing inside it.
    round?: boolean
    // Left edge the text box may not cross, in `host` coordinates. Defaults to a small margin, which
    // is right for a host that is the whole screen; a right-docked panel's container starts hundreds
    // of pixels in, so its clues legitimately sit at a negative x and pass a negative bound.
    boxMinX?: number
    boxMaxX?: number
    boxMinY?: number
    boxMaxY?: number
    // Dim this area of `host` while the clue is up, except where the control is, and light the
    // control up rather than bracketing it. The inventory panel passes its own board.
    spotlight?: ClueSpotlightArea
    // What the spotlight leaves undimmed, when that is more than the control: a recipe's whole row,
    // so the player can see what the lit Craft button will make.
    reveal?: ClueRect
    // The note's words when this control says the clue's step differently: the crafting screen points
    // the pack clue at its Pack tab, where "here it is" would be pointing at the wrong thing.
    lines?: string[]
}

// A CountdownTimer, structurally, for the one clue that points at the wave dial. Typed rather than
// imported for the same reasons as ClueChip below, and because the dial is reached through the
// GuiManager rather than handed to the constructor: it is a permanent part of the HUD that nothing
// has to report, like the ready bar.
interface ClueDial {
    visible: boolean
    x: number
    y: number
    dialSize: number
}

// A HudChip, structurally. Typed rather than imported so this file doesn't depend on the chip (and so
// a test can hand in a stub).
interface ClueChip {
    visible: boolean
    x: number
    y: number
    getWidth: () => number
    getHeight: () => number
    parent?: Container
}

// The ready bar, by SHAPE rather than as the EquippedWidget it always is in the game (gui/equipped.ts),
// for the same reason ClueChip above is structural: it keeps this file off the sprite graph. The wood
// slot is optional because only the real widget tracks one -- a bar with no wood readied, and any
// stand-in for it, simply has no slot to point at, which builtInClueAnchor already treats as "nothing
// to anchor to".
type ReadyBar = Graphics & { getWoodSlotBounds?: () => Rectangle | undefined }

// One clue on screen, as the frame clock poses it: the note, the brackets round its control (if it
// has one), when it arrived and -- once it is going -- when it started to go.
export interface LiveClue {
    note: ClueCallout
    ring?: ClueMark
    // The panel this clue dims, and the control it cuts the dimming away from.
    spotlight?: { host: Container; area: ClueSpotlightArea; target: ClueRect }
    shownAt: number
    leftAt?: number
    // Where the note rests, and which way it slides in from (a unit step AWAY from its control).
    restX: number
    restY: number
    slideX: number
    slideY: number
    remove: () => void
}

// The unit step away from the control, per side: a note slides its last few pixels TOWARD what it
// is pointing at.
const SLIDE_FROM: Record<CluePlacement, [number, number]> = {
    left: [-1, 0],
    right: [1, 0],
    above: [0, -1],
    below: [0, 1],
}

export class Tutorial {
    gui: GuiManager
    surface: Graphics
    equippedWidget: ReadyBar
    controlBar: Graphics
    minimapSurface: Graphics
    // The camp's stand-in for the control bar's inventory button (ember rooms suppress the bar), and
    // therefore what the first clue points at. Optional so a test can leave it out.
    emberInventoryChip?: ClueChip
    activeTutorials: Set<TutorialType> = new Set()
    // Per-room suppression latch (set from GuiManager.roomUpdated via WorldState.visibility). When
    // true, setup() is a no-op so no tutorial popups appear — ember rooms suppress them. It does NOT
    // hold back the ember clues: see setup().
    suppressed: boolean = false

    // The ember clues' bookkeeping (armed vs anchored, expiry, teardown) lives next door in
    // ember_clues.ts, free of pixi; all this file provides is the drawing.
    private clues: EmberClueDirector<ClueAnchor> = new EmberClueDirector<ClueAnchor>({
        draw: (tutorialType, anchor) => this.drawClue(tutorialType, anchor),
        builtInAnchor: tutorialType => this.builtInClueAnchor(tutorialType) || undefined,
    })

    // The frame clock every clue's motion is posed from: GuiManager.updateTimer -> animate. Motion is
    // a pure function of it (clue_callout.ts), so there is no timer per clue and nothing to stop.
    private buildingDemo?: BuildingTutorial
    private buildingDemoPendingUntilInventoryClose = false
    private buildingDemoShownLocally = false
    private demolitionDemo?: BuildingTutorial
    // Chapter two's: how to open the forager's wheel and give him an order.
    private foragerDemo?: ForagerTutorial
    private sprintDemo?: SprintTutorial
    // Chapter five's: pick the ladder, walk into the shelter, put it down in the north-west corner.
    private ladderDemo?: LadderTutorial
    // Sent while the pack or crafting is open (the ladder was just crafted): it waits for that to close.
    private ladderDemoPendingUntilItemScreenClose = false
    // Where the helper windows go, with the dimming over the rest of the screen under them. Over
    // the whole HUD in the game (GuiManager's tutorial demo layer); the HUD surface when none is given.
    private demoSurface: Container
    private demoScrim?: ClueScrim

    private demos = (): TutorialDemo[] => [this.buildingDemo, this.demolitionDemo, this.foragerDemo, this.sprintDemo, this.ladderDemo]
        .filter(demo => demo && !demo.destroyed)

    /** The helper window's global bounds while one is up, for toasts to keep clear of. */
    demoBounds = () => {
        const demo = this.demos().find(next => next.visible)
        return demo?.getBounds()
    }

    isForagerDemoShowing = () => !!this.foragerDemo && !this.foragerDemo.destroyed

    // The player has given a forager an order: the forager helper has made its point, so it goes.
    // Recorded as the player closing it: a replay acts out the close, not the order.
    foragerOrdered = () => {
        if (this.foragerDemo?.leave()) this.reportUse("ember-forager-demo", { action: "close" })
    }

    // Each helper is up on the demo layer, and tells the session recording what the player's own
    // presses did to it (TutorialDemo.onPlayerUse): a replay does the same to its copy.
    private mountDemo = <T extends TutorialDemo>(demo: T, tutorialType: TutorialType): T => {
        demo.onPlayerUse = use => this.reportUse(tutorialType, use)
        this.demoSurface.addChild(demo)
        return demo
    }

    // Only for the recording: a helper or clue the player closed (or stepped) on their own screen
    // sends nothing else, and a replay would leave it up. Nothing is sent from a replay itself.
    private reportUse = (tutorialType: TutorialType, use: TutorialUse) => {
        this.gui.logic?.playerController?.client?.sendTelemetry?.({ kind: "tutorial", tutorial: tutorialType, ...use })
    }

    // Which helper window each tutorial type puts up. The roof lesson's helper is the building one.
    private demoSlots: Partial<Record<TutorialType, () => TutorialDemo | undefined>> = {
        "ember-building-demo": () => this.buildingDemo,
        "ember-demolition-demo": () => this.demolitionDemo,
        "ember-forager-demo": () => this.foragerDemo,
        "ember-sprint-demo": () => this.sprintDemo,
        "ember-ladder-demo": () => this.ladderDemo,
    }

    // How each clue up on screen is dismissed, as its own X does it. One per type: setupTutorial
    // never draws a second clue of a type that is already live.
    private dismissals: Map<TutorialType, () => void> = new Map()

    /**
     * Replay only (GuiManager.actOutRecordedAnswer): the recorded player did this to a tutorial on
     * their screen. A clue that is not up in the replay at that moment is still disarmed, as the
     * player's X disarmed it, so the next rebuild of its panel does not put it back.
     */
    actOutPlayerUse = (tutorialType: TutorialType, use: TutorialUse) => {
        const slot = this.demoSlots[tutorialType]
        if (slot) {
            const demo = slot()
            if (demo && !demo.destroyed) demo.actOut(use)
            return
        }
        if (use.action !== "close") return
        const dismiss = this.dismissals.get(tutorialType)
        if (dismiss) dismiss()
        else if (isEmberClueType(tutorialType)) this.clues.disarm(tutorialType)
    }
    private clock = 0
    private live: Set<LiveClue> = new Set()
    // When each clue was last taken down to be REDRAWN, and when it had first arrived. The inventory
    // panel rebuilds -- clue and all -- on every server message while it is open, and a clue redrawn
    // within the same frame carries on where it was instead of arriving all over again.
    private lastShown: Map<TutorialType, { shownAt: number; at: number }> = new Map()
    // How dimmed the spotlit panel is, 0..1. ONE level rather than one per clue, because the panel
    // rebuilds -- host and all -- on every server message and on every step of the lesson: a clue
    // handing over to the next one keeps the panel dark instead of flashing it light for a frame.
    private spotlightLevel = 0
    // The dimming drawn into each host. One per host however many of its controls are lit.
    private scrims: Map<Container, ClueScrim> = new Map()

    constructor(
        gui: GuiManager,
        surface: Graphics,
        equippedWidget: ReadyBar,
        controlBar: Graphics,
        minimapSurface: Graphics,
        emberInventoryChip?: ClueChip,
        demoSurface?: Container,
    ) {
        this.gui = gui
        this.surface = surface
        this.equippedWidget = equippedWidget
        this.controlBar = controlBar
        this.minimapSurface = minimapSurface
        this.emberInventoryChip = emberInventoryChip
        this.demoSurface = demoSurface || surface
    }

    // Advance every clue on screen. Costs one addition when there are none, which is nearly always.
    // The clock runs regardless: it is also what tells a redraw within the frame (carry on) from a
    // control that went away and came back (arrive again).
    animate = (deltaMs: number) => {
        if (this.sprintDemo && !this.sprintDemo.destroyed) {
            this.sprintDemo.animate(deltaMs)
        }
        if (this.buildingDemo && !this.buildingDemo.destroyed) this.buildingDemo.animate(deltaMs)
        if (this.demolitionDemo && !this.demolitionDemo.destroyed) {
            this.demolitionDemo.animate(deltaMs)
        }
        if (this.foragerDemo && !this.foragerDemo.destroyed) this.foragerDemo.animate(deltaMs)
        if (this.ladderDemo && !this.ladderDemo.destroyed) this.ladderDemo.animate(deltaMs)
        if (this.demoScrim || this.demos().length > 0) this.poseDemoScrim()
        const step = Math.min(MAX_FRAME_MS, Math.max(0, deltaMs || 0))
        this.clock += step
        if (this.live.size > 0) {
            this.live.forEach(this.pose)
        }
        if (this.scrims.size > 0 || this.spotlightLevel > 0) {
            this.poseSpotlight(step)
        }
    }

    // While a helper window is up, everything else on screen -- the camp and the whole HUD -- sits
    // under the same dimming the pack's board takes behind a taught control. It comes and goes with
    // the window itself (its arrival and departure fade), so there is no level of its own to keep.
    private poseDemoScrim = () => {
        const level = this.demos().reduce((most, demo) => Math.max(most, demo.visible ? demo.alpha : 0), 0)
        if (!(level > 0)) {
            if (this.demoScrim && !this.demoScrim.destroyed) {
                this.demoScrim.parent?.removeChild(this.demoScrim)
                this.demoScrim.destroy()
            }
            this.demoScrim = undefined
            return
        }
        if (!this.demoScrim || this.demoScrim.destroyed) this.demoScrim = new ClueScrim()
        const scrim = this.demoScrim
        // The whole stage viewport, past its edges, wherever the layer happens to sit.
        const { left, top, width, height } = getUiMetrics().viewport
        const from = this.demoSurface.toLocal(new Point(left - DEMO_SCRIM_BLEED, top - DEMO_SCRIM_BLEED), this.gui.stage)
        const to = this.demoSurface.toLocal(new Point(left + width + DEMO_SCRIM_BLEED, top + height + DEMO_SCRIM_BLEED), this.gui.stage)
        const area = { x: from.x, y: from.y, w: to.x - from.x, h: to.y - from.y }
        const drawn = scrim.area
        if (!drawn || drawn.x !== area.x || drawn.y !== area.y || drawn.w !== area.w || drawn.h !== area.h) scrim.draw(area, [])
        scrim.alpha = level
        // Under the windows.
        if (scrim.parent !== this.demoSurface) this.demoSurface.addChildAt(scrim, 0)
        else if (this.demoSurface.getChildIndex(scrim) !== 0) this.demoSurface.setChildIndex(scrim, 0)
    }

    // The panel dims while a control in it is being taught, and lifts when the last such clue goes.
    // A scrim whose host has nothing lit any more (its menu closed under it) fades with the level
    // and goes once it is clear; one whose host was thrown away by a rebuild goes at once.
    private poseSpotlight = (step: number) => {
        const lit = Array.from(this.live).some(clue => clue.spotlight && clue.leftAt === undefined)
        this.spotlightLevel = lit
            ? Math.min(1, this.spotlightLevel + step / SPOTLIGHT_IN_MS)
            : Math.max(0, this.spotlightLevel - step / SPOTLIGHT_OUT_MS)
        const alpha = this.scrimAlpha()
        this.scrims.forEach((scrim, host) => {
            if (scrim.destroyed || host.destroyed) {
                scrim.undim()
                this.scrims.delete(host)
                return
            }
            if (this.spotlightLevel === 0 && !this.hasSpotlitClue(host)) {
                this.dropScrim(host)
                return
            }
            scrim.setLevel(alpha)
        })
    }

    private scrimAlpha = () => {
        const t = this.spotlightLevel
        return t * t * (3 - 2 * t)
    }

    private hasSpotlitClue = (host: Container) =>
        Array.from(this.live).some(clue => clue.spotlight?.host === host)

    private dropScrim = (host: Container) => {
        const scrim = this.scrims.get(host)
        this.scrims.delete(host)
        scrim?.undim()
        if (scrim && !scrim.destroyed) {
            scrim.parent?.removeChild(scrim)
            scrim.destroy()
        }
    }

    // Redraw a host's dimming for the clues lit in it now: one cut per control. With none left, the
    // scrim stays (whole, no cuts) to fade out with the level, unless another host has taken the
    // spotlight over -- a rebuilt panel -- in which case the old one is not on screen to fade.
    private syncScrim = (host: Container) => {
        const clues = Array.from(this.live).filter(clue => clue.spotlight?.host === host)
        if (clues.length === 0) {
            const handedOver = Array.from(this.live).some(clue => clue.spotlight && clue.spotlight.host !== host)
            if (handedOver || host.destroyed) {
                this.dropScrim(host)
            } else {
                const scrim = this.scrims.get(host)
                if (scrim?.area && !scrim.destroyed) scrim.draw(scrim.area, [])
            }
            return
        }
        // A newer host means the panel was rebuilt: the old scrim went with the old tree.
        Array.from(this.scrims.keys()).filter(other => other !== host).forEach(this.dropScrim)
        const area = clues[clues.length - 1].spotlight!.area
        let scrim = this.scrims.get(host)
        if (!scrim || scrim.destroyed) {
            scrim = new ClueScrim()
            this.scrims.set(host, scrim)
        }
        scrim.draw(area, spotlightHoles(area, clues.map(clue => clue.spotlight!.target)))
        scrim.setLevel(this.scrimAlpha())
        // Under everything the clues draw: the lit frames and the notes stand on it.
        if (scrim.parent !== host) host.addChildAt(scrim, 0)
        else host.setChildIndex(scrim, 0)
    }

    private pose = (clue: LiveClue) => {
        if (clue.note.destroyed) {
            // Its host was torn down under it without a stop (nothing should, but a clue is never
            // worth an exception in the frame loop).
            this.live.delete(clue)
            return
        }
        const shown = this.clock - clue.shownAt
        const left = clue.leftAt === undefined ? undefined : this.clock - clue.leftAt
        clue.ring?.poseAt(shown, left)
        const note = clueNotePose(shown, left)
        clue.note.alpha = note.alpha
        clue.note.x = clue.restX + clue.slideX * note.slide
        clue.note.y = clue.restY + clue.slideY * note.slide
        if (left !== undefined && left >= CLUE_LEAVE_MS) {
            clue.remove()
        }
    }

    // Builds the note (and, given a control's size, the brackets that go round it) every tutorial
    // and clue is made of, and how to take them down. Placing them is the caller's business.
    // `timeoutMs` of 0 means it does not take itself down: the ember clues have their own lifetime
    // (see ember_clues.ts).
    setupTutorial = (
        tutorialType: TutorialType,
        text: string[],
        ringTarget?: ClueRingTarget,
        timeoutMs: number = POPUP_TIMEOUT_MS,
    ): { note: ClueCallout; ring?: ClueMark; clue: LiveClue; stop: ClueStop } | undefined => {
        if (this.activeTutorials.has(tutorialType)) {
            return undefined
        }
        this.activeTutorials.add(tutorialType)

        // While the pointer is on the note the world does not get the mouse, so a click meant for
        // the note (or for its dismiss X) is not also a swing at whatever is behind it. Tracked here
        // rather than left to pixi so the teardown can hand the mouse back (see stop), and so a note
        // never clears a latch it did not set.
        let hovered = false
        const setHovered = (over: boolean) => {
            if (hovered === over) {
                return
            }
            hovered = over
            this.gui.mouseOverControl(over)
        }

        // Dismissing a CLUE has to disarm it as well as take it down, or the next rebuild of the panel
        // it points at would put it straight back up over a player who has said they are done with it.
        const dismiss = () => {
            stop({ fade: true })
            if (isEmberClueType(tutorialType)) {
                this.clues.disarm(tutorialType)
            }
        }
        this.dismissals.set(tutorialType, dismiss)
        const note = new ClueCallout({
            lines: text,
            onDismiss: () => {
                this.reportUse(tutorialType, { action: "close" })
                dismiss()
            },
        })
        note.interactive = true
        note.on("mouseover", () => setHovered(true))
        note.on("mouseout", () => setHovered(false))

        const ring: ClueMark | undefined = !ringTarget ? undefined
            : ringTarget.lit ? new ClueLight(ringTarget) : new ClueRing(ringTarget)
        const previous = this.lastShown.get(tutorialType)
        // Removal can be asked for twice -- the frame clock finishing a fade, then the fallback timer
        // behind it -- and a second destroy() on a Graphics throws.
        let removed = false
        const clue: LiveClue = {
            note,
            ring,
            shownAt: previous && previous.at === this.clock ? previous.shownAt : this.clock,
            restX: 0,
            restY: 0,
            slideX: 0,
            slideY: 0,
            remove: () => {
                if (removed) {
                    return
                }
                removed = true
                this.live.delete(clue)
                if (clue.spotlight) this.syncScrim(clue.spotlight.host)
                ring?.parent?.removeChild(ring)
                ring?.destroy()
                note.parent?.removeChild(note)
                // Its Text textures are per-instance and not reclaimed by GC.
                if (!note.destroyed) {
                    note.destroy({ children: true, texture: true, baseTexture: true })
                }
            },
        }
        this.live.add(clue)

        // Idempotent: a clue can be taken down by its own dismiss X, by its expiry timer, by the panel
        // it points at being rebuilt, and by the tutorial being marked seen -- and more than one of
        // those can land on the same clue.
        let stopped = false
        const stop: ClueStop = opts => {
            if (stopped) {
                return
            }
            stopped = true
            // Give the world its mouse back FIRST. The note takes it away while the pointer is on it,
            // and the commonest way for one to go is the player clicking its own dismiss X -- with the
            // pointer right there on it. Removing a display object does not make pixi deliver the
            // mouseout it owed, so the latch would stay set and the player would be left unable to
            // walk or swing until the next room load. The expiry timer and a panel rebuild can land
            // under a resting pointer the same way.
            setHovered(false)
            this.activeTutorials.delete(tutorialType)
            if (this.dismissals.get(tutorialType) === dismiss) this.dismissals.delete(tutorialType)
            note.interactive = false
            note.interactiveChildren = false
            // Going for good, and still on screen: let the frame clock fade it. Otherwise -- a redraw,
            // or the control it named has just gone with its panel -- at once, so nothing is left
            // pointing at where a control used to be.
            if (opts?.fade && note.parent && note.worldVisible) {
                clue.leftAt = this.clock
                // The frame clock stops in a hidden tab; the clue must still go.
                setTimeout(clue.remove, CLUE_LEAVE_MS + 500)
                return
            }
            this.lastShown.set(tutorialType, { shownAt: clue.shownAt, at: this.clock })
            clue.remove()
        }

        if (timeoutMs > 0) {
            setTimeout(() => stop({ fade: true }), timeoutMs)
        }

        return { note, ring, clue, stop }
    }

    // Puts a note beside a control and brackets round it -- every clue, and every overworld popup that
    // has a control to point at -- and hands back how to take them down.
    private pointAt = (
        tutorialType: TutorialType,
        text: string[],
        anchor: ClueAnchor,
        timeoutMs: number,
    ): ClueStop | undefined => {
        const lit = !!anchor.spotlight
        const built = this.setupTutorial(tutorialType, text, { w: anchor.w, h: anchor.h, round: anchor.round, lit }, timeoutMs)
        if (!built) {
            return undefined
        }
        const { note, ring, clue, stop } = built
        if (anchor.spotlight) {
            const target = anchor.reveal || { x: anchor.x, y: anchor.y, w: anchor.w, h: anchor.h }
            clue.spotlight = { host: anchor.host, area: anchor.spotlight, target }
        }
        // Brackets under the note: where the two meet, the words win.
        if (ring) {
            ring.x = anchor.x
            ring.y = anchor.y
            anchor.host.addChild(ring)
        }
        anchor.host.addChild(note)

        const placement = anchor.placement || "left"
        const box = note.noteRect()
        const { boxX, boxY } = clueBoxLayout({
            anchorX: anchor.x,
            anchorY: anchor.y,
            anchorW: anchor.w,
            anchorH: anchor.h,
            boxW: box.width,
            boxH: box.height,
            boxMinX: anchor.boxMinX,
            boxMaxX: anchor.boxMaxX,
            placement,
        })
        // ...then held inside the host's visible screen, which a scaled or docked host reports.
        const clamp = (value: number, min: number | undefined, max: number | undefined, size: number) => {
            const lower = min === undefined ? -Infinity : min
            const upper = max === undefined ? Infinity : max - size
            return Math.max(lower, Math.min(value, Math.max(lower, upper)))
        }
        const x = Math.round(clamp(boxX, anchor.boxMinX, anchor.boxMaxX, box.width))
        const y = Math.round(clamp(boxY, anchor.boxMinY, anchor.boxMaxY, box.height))

        note.setNotch(clueNotch({
            placement,
            boxX: x,
            boxY: y,
            boxW: box.width,
            boxH: box.height,
            anchorX: anchor.x,
            anchorY: anchor.y,
            anchorW: anchor.w,
            anchorH: anchor.h,
        }))
        clue.restX = x
        clue.restY = y
        ;[clue.slideX, clue.slideY] = SLIDE_FROM[placement]
        this.pose(clue)
        if (clue.spotlight) this.syncScrim(anchor.host)
        return stop
    }

    // The overworld popups: one notice each, raised by the intro campaign's own triggers.
    setupHotbarTutorial = () => {
        const anchor = this.builtInClueAnchor("ember-ready")
        if (!anchor) return
        this.pointAt("hotbar", [
            "This is your hotbar.",
            "Activate readied equipment, supplies, and follower commands",
            "by pressing their associated numbers and letters (1, 2, J, K ...).",
        ], anchor, POPUP_TIMEOUT_MS)
    }

    setupMapTutorial = () => {
        const map = this.minimapSurface
        if (!map) return
        this.pointAt("map", [
            "This is your map.",
            "Points of interest appear as icons, and new ones pulse to get your attention.",
        ], {
            host: this.surface,
            ...this.viewportLimits(this.surface),
            x: map.x,
            y: map.y,
            w: map.width,
            h: map.height,
        }, POPUP_TIMEOUT_MS)
    }

    setupControlbarTutorial = () => {
        const anchor = this.builtInClueAnchor("ember-items")
        if (!anchor) return
        this.pointAt("controlbar", [
            "Access your inventory and craft items using these controls.",
        ], anchor, POPUP_TIMEOUT_MS)
    }

    setupCompanionTutorial = () => {
        const playerEntity = this.gui.logic.playerEntity
        if (!playerEntity) {
            this.activeTutorials.delete("companion")
            return
        }

        const companion = this.gui.logic.entityManager.find(e => e.leaderEntityId === playerEntity.entityId)
        if (!companion) {
            this.activeTutorials.delete("companion")
            return
        }

        const entityRenderable = this.gui.logic.clientRenderer.map().overlay().getEntityRenderable(companion.entityId)
        if (!entityRenderable) {
            this.activeTutorials.delete("companion")
            return
        }

        const tutorial = this.setupTutorial("companion", [
            "This is your companion and protected.",
            "Mouse over him, and click the icons that appear to interact.",
        ])
        if (!tutorial) {
            return
        }
        // Hung off the companion's own renderable so it walks with him: the note to his left, its
        // notch pointing across at him. No brackets -- a body is not a control.
        const { note, clue } = tutorial
        entityRenderable.addChild(note)
        const box = note.noteRect()
        clue.restX = -box.width - CLUE_NOTCH_LEN - 2
        clue.restY = -25
        ;[clue.slideX, clue.slideY] = SLIDE_FROM.left
        note.setNotch({ edge: "right", tipX: box.width + CLUE_NOTCH_LEN, tipY: box.height / 2 })
        this.pose(clue)
    }

    // =========================================================================================
    // THE EMBER CLUES
    //
    // Five pointer-and-text boxes that name the controls the camp's first fire is built with. They are
    // armed by the SERVER (assets/scripts/v2/ember-tutorial.js), one beat at a time, because only the
    // director knows which beat is live -- the client cannot tell "waiting for a campfire to be
    // crafted" from "the player is browsing recipes for fun".
    //
    // The rules (armed vs anchored, lifetime, teardown) are in ember_clues.ts. This half draws them.
    // =========================================================================================

    // Whatever draws one of the clued controls says where it landed. Called again on every rebuild of
    // the panel it lives in.
    registerClueAnchor = (tutorialType: EmberClueType, anchor: ClueAnchor) =>
        this.clues.registerAnchor(tutorialType, anchor)

    // The control has gone (its panel closed, or this rebuild no longer draws it).
    clearClueAnchor = (tutorialType: EmberClueType) => this.clues.clearAnchor(tutorialType)

    // The player DID the thing this clue was pointing at, so it has served its purpose and goes for
    // good. Disarm rather than merely retract: a satisfied clue must not come back when the panel it
    // pointed at is next rebuilt, or reopening the pack would re-light a label for a step already done.
    satisfyClue = (tutorialType: EmberClueType) => this.clues.disarm(tutorialType)

    // Opening either item screen satisfies the hints pointing at the Items pill, regardless of
    // which controls or recipes the panel happens to draw.
    itemScreenOpened = () => {
        for (const type of ["ember-items", "ember-roof-items", "ember-wood-items"] as EmberClueType[]) {
            this.satisfyClue(type)
        }
    }

    // Every clue down and disarmed: the tutorial is over, or the camp is.
    private clearBuildingDemo = () => {
        this.buildingDemoPendingUntilInventoryClose = false
        if (this.buildingDemo && !this.buildingDemo.destroyed) this.buildingDemo.destroy()
        this.buildingDemo = undefined
        this.poseDemoScrim()
    }
    itemScreenClosed = () => {
        if (this.ladderDemoPendingUntilItemScreenClose) {
            this.ladderDemoPendingUntilItemScreenClose = false
            this.showLadderDemo()
        }
        if (!this.buildingDemoPendingUntilInventoryClose) return
        this.buildingDemoPendingUntilInventoryClose = false
        if (this.buildingDemoShownLocally || this.gui.logic?.playerEntity?.flags?.emberBuildingTutorialShown) return
        if (!this.buildingDemo || this.buildingDemo.destroyed) {
            this.buildingDemo = this.mountDemo(
                new BuildingTutorial(over => this.gui.mouseOverControl(over), this.clearBuildingDemo), "ember-building-demo")
            this.buildingDemoShownLocally = true
            this.gui.logic.playerController.client.emitPlayerEvent({ playerEventType: "EMBER_BUILDING_TUTORIAL_SHOWN" })
        }
    }
    private clearForagerDemo = () => {
        if (this.foragerDemo && !this.foragerDemo.destroyed) this.foragerDemo.destroy()
        this.foragerDemo = undefined
        this.poseDemoScrim()
    }
    private clearDemolitionDemo = () => {
        if (this.demolitionDemo && !this.demolitionDemo.destroyed) this.demolitionDemo.destroy()
        this.demolitionDemo = undefined
        this.poseDemoScrim()
    }
    private showLadderDemo = () => {
        if (this.ladderDemo && !this.ladderDemo.destroyed) return
        this.ladderDemo = this.mountDemo(new LadderTutorial(over => this.gui.mouseOverControl(over), this.clearLadderDemo,
            undefined, this.gui.logic?.playerEntity), "ember-ladder-demo")
    }
    private clearLadderDemo = () => {
        this.ladderDemoPendingUntilItemScreenClose = false
        if (this.ladderDemo && !this.ladderDemo.destroyed) this.ladderDemo.destroy()
        this.ladderDemo = undefined
        this.poseDemoScrim()
    }
    private clearSprintDemo = () => {
        if (this.sprintDemo && !this.sprintDemo.destroyed) this.sprintDemo.destroy()
        this.sprintDemo = undefined
        this.poseDemoScrim()
    }
    clearEmberClues = () => {
        this.clues.clearAll(); this.clearBuildingDemo(); this.clearDemolitionDemo(); this.clearForagerDemo(); this.clearSprintDemo()
        this.clearLadderDemo()
    }

    isClueArmed = (tutorialType: EmberClueType): boolean => this.clues.isArmed(tutorialType)

    isClueShowing = (tutorialType: EmberClueType): boolean => this.clues.isShowing(tutorialType)

    isAnyClueShowing = (): boolean => this.clues.anyShowing()

    // Draw one clue at its anchor, and hand back how to take it down.
    private drawClue = (tutorialType: EmberClueType, anchor: ClueAnchor): ClueStop | undefined =>
        this.pointAt(tutorialType, anchor.lines ?? CLUE_TEXT[tutorialType], anchor, 0)


    // The one clue whose control is part of the permanent HUD rather than a panel, so it needs no
    // registration: in a CAMP the pack is opened from the ITEMS chip in the top-right row, because
    // ember rooms suppress the control bar (GuiManager.roomUpdated). The bar's own inventory.png --
    // the first entry in ControlBar.setup() -- is the fallback for a room that leaves the bar up.
    private builtInClueAnchor = (tutorialType: EmberClueType): ClueAnchor | null => {
        // The pre-wave COUNTDOWN DIAL, which is the deadline the run hint is about: it is up for the
        // ten seconds before a wave spawns, with nothing else happening on screen, and it goes away
        // by itself when the wave lands.
        //
        // DESKTOP ONLY. On a phone the run is the move stick pushed out to the rim
        // (client_util's sprintScaledIntent) -- there is no persistent control to ring, and a thumb
        // that commits a direction fully discovers it without being told.
        if (tutorialType === "ember-run") {
            if (isMobile()) {
                return null
            }
            const dial = this.countdownDial
            if (!dial || !dial.visible || !(dial.dialSize > 0)) {
                return null
            }
            const size = dial.dialSize
            // The first anchor that points at a widget living on the STAGE rather than in the HUD
            // container the clues are drawn into, so its position has to come back across that
            // offset (GuiManager.init sets container.x; the chip layer copies it for the same
            // reason). The dial also positions itself by its CENTRE, not its corner.
            return {
                host: this.surface,
                ...this.viewportLimits(this.surface),
                x: dial.x - size / 2 - (this.surface.x || 0),
                y: dial.y - size / 2 - (this.surface.y || 0),
                w: size,
                h: size,
                placement: "right",
                round: true,
            }
        }
        // The ready bar, which is the equipped widget: permanent HUD, so nothing has to report it.
        if (tutorialType === "ember-ready" || tutorialType === "ember-wood") {
            if (!this.equippedWidget || !this.equippedWidget.visible) {
                return null
            }
            const bounds = tutorialType === "ember-wood"
                ? this.equippedWidget.getWoodSlotBounds?.()
                : this.equippedWidget.getLocalBounds()
            if (!bounds) return null
            const host = this.equippedWidget.parent || this.surface
            return {
                host,
                ...this.viewportLimits(host),
                x: this.equippedWidget.x + bounds.x * this.equippedWidget.scale.x,
                y: this.equippedWidget.y + bounds.y * this.equippedWidget.scale.y,
                w: bounds.width * this.equippedWidget.scale.x,
                h: bounds.height * this.equippedWidget.scale.y,
                placement: "above",
            }
        }
        if (tutorialType !== "ember-items" && tutorialType !== "ember-roof-items" && tutorialType !== "ember-wood-items") {
            return null
        }
        const chip = this.emberInventoryChip
        if (chip && chip.visible && (!chip.parent || chip.parent.worldVisible)) {
            const host = chip.parent || this.surface
            return { host, ...this.viewportLimits(host), x: chip.x, y: chip.y, w: chip.getWidth(), h: chip.getHeight() }
        }
        if (!this.controlBar || !this.controlBar.visible) {
            return null
        }
        return {
            host: this.surface,
            x: this.controlBar.x + CONTROLBAR_ITEMS_OFFSET_X,
            y: this.controlBar.y,
            w: CONTROLBAR_ITEMS_W,
            h: CONTROLBAR_ITEMS_H,
        }
    }

    private viewportLimits = (host: Container): Partial<ClueAnchor> => {
        if (!this.gui.stage) return {}
        const safe = getUiMetrics().safeViewport
        const topLeft = host.toLocal(new Point(safe.left, safe.top), this.gui.stage)
        const bottomRight = host.toLocal(new Point(safe.right, safe.bottom), this.gui.stage)
        return { boxMinX: topLeft.x + 8, boxMaxX: bottomRight.x - 8,
            boxMinY: topLeft.y + 8, boxMaxY: bottomRight.y - 8 }
    }

    // The wave countdown dial, read off the GuiManager rather than taken in the constructor: it is
    // permanent HUD furniture like the ready bar, and a GuiManager stub in a test simply leaves it
    // undefined, which builtInClueAnchor already treats as "nothing to anchor to".
    private get countdownDial(): ClueDial | undefined {
        return (this.gui as unknown as { countdownTimer?: ClueDial }).countdownTimer
    }

    private hudAnchors = new Map<EmberClueType, ClueAnchor>()
    refreshHudAnchors = () => {
        for (const type of ["ember-run", "ember-ready", "ember-wood", "ember-items", "ember-roof-items", "ember-wood-items"] as EmberClueType[]) {
            const anchor = this.builtInClueAnchor(type)
            const previous = this.hudAnchors.get(type)
            if (!anchor) {
                this.clues.clearAnchor(type)
                this.hudAnchors.delete(type)
            } else if (!previous || Object.keys(anchor).some(key => anchor[key] !== previous[key])) {
                this.hudAnchors.set(type, anchor)
                this.clues.registerAnchor(type, anchor)
            }
        }
    }

    // The one way in from the wire (GuiManager.showTutorial). What each kind of message means -- and
    // which of them `suppressed` is allowed to swallow -- is decided in one place, routeTutorial.
    setup = (tutorialType: TutorialType | null, timeoutMs?: number) => {
        this.refreshHudAnchors()
        if (tutorialType === "ember-sprint-demo") {
            // Chapter timing belongs to the server; private player flags are not on the wire.
            if (!this.sprintDemo) {
                this.sprintDemo = this.mountDemo(new SprintTutorial(over => this.gui.mouseOverControl(over), this.clearSprintDemo,
                    undefined, this.gui.logic?.playerEntity), "ember-sprint-demo")
            }
            return
        }
        if (tutorialType === "ember-demolition-demo") {
            // Chapter timing belongs to the server; private player flags are not on the wire.
            if (!this.demolitionDemo) {
                this.demolitionDemo = this.mountDemo(new BuildingTutorial(over => this.gui.mouseOverControl(over),
                    this.clearDemolitionDemo, "demolish"), "ember-demolition-demo")
            }
            return
        }
        if (tutorialType === "ember-roof-demo") {
            if (this.buildingDemoShownLocally || this.gui.logic?.playerEntity?.flags?.emberBuildingTutorialShown) return
            this.buildingDemoPendingUntilInventoryClose = true
            return
        }
        if (tutorialType === "ember-building-demo") {
            if (!this.buildingDemo) {
                this.buildingDemo = this.mountDemo(
                    new BuildingTutorial(over => this.gui.mouseOverControl(over), this.clearBuildingDemo), "ember-building-demo")
            }
            return
        }
        if (tutorialType === "ember-ladder-demo") {
            // Chapter timing belongs to the server. Over the crafting screen the ready bar it points at is
            // out of reach, so it waits for that screen to close.
            if (this.gui.isItemScreenOpen?.()) this.ladderDemoPendingUntilItemScreenClose = true
            else this.showLadderDemo()
            return
        }
        if (tutorialType === "ember-forager-demo") {
            if (!this.foragerDemo) {
                this.foragerDemo = this.mountDemo(
                    new ForagerTutorial(over => this.gui.mouseOverControl(over), this.clearForagerDemo), "ember-forager-demo")
            }
            return
        }
        const route = routeTutorial(tutorialType, this.suppressed)
        if (route === "clear") {
            // Step changes leave the inventory open; its controls still have valid anchors.
            // Room changes use clearEmberClues() to discard those anchors as well.
            this.clues.clearAll(true)
            this.clearBuildingDemo()
            this.clearDemolitionDemo()
            this.clearForagerDemo()
            this.clearSprintDemo()
            this.clearLadderDemo()
            return
        }
        if (route === "suppressed") {
            return
        }
        if (route === "clue") {
            this.clues.arm(tutorialType as EmberClueType, timeoutMs)
            return
        }
        const all = false
        if (all || tutorialType === "controlbar") {
            this.setupControlbarTutorial()
        }
        if (all || tutorialType === "hotbar") {
            this.setupHotbarTutorial()
        }
        if (all || tutorialType === "map") {
            this.setupMapTutorial()
        }
        if (all || tutorialType === "companion") {
            setTimeout(() => {
                this.setupCompanionTutorial()
            }, 4000)
        }
    }
}
